import React, { useState } from 'react';
import { StorySeed, StoryMode, ChapterLength, WeightedGenre, GenreWeight } from '../src/types';
import Tooltip from './Tooltip';

interface StorySetupProps {
  onInitialize: (seed: StorySeed) => void;
  isLoading: boolean;
  hasSavedGame: boolean;
  onResume: () => void;
}

const GENRE_CATEGORIES = [
  {
    name: 'Romance',
    options: ['Contemporary Romance', 'Fantasy Romance', 'Romantasy', 'Paranormal Romance', 'Romantic Suspense', 'Historical Romance', 'Dark Romance', 'Small Town Romance', 'Second Chance Romance', 'Enemies to Lovers', 'Billionaire Romance', 'Spicy Romance']
  },
  {
    name: 'Fantasy',
    options: ['Epic Fantasy', 'Dark Fantasy', 'Cozy Fantasy', 'LitRPG', 'Progression Fantasy', 'Academy Fantasy', 'Portal Fantasy', 'High Fantasy', 'Urban Fantasy', 'Grimdark', 'Magical Realism']
  },
  {
    name: 'Mystery & Thriller',
    options: ['Cozy Mystery', 'Detective Mystery', 'Crime Thriller', 'Legal Thriller', 'Domestic Thriller', 'Political Thriller', 'Serial Killer Thriller', 'Psychological Thriller']
  },
  {
    name: 'Sci-Fi',
    options: ['Dystopian', 'Alien Contact', 'Sci-Fi Romance', 'Cyberpunk', 'Space Opera', 'Post-Apocalyptic', 'Time Travel', 'Military Sci-Fi']
  },
  {
    name: 'Horror',
    options: ['Psychological Horror', 'Monster Horror', 'Dark Fantasy Horror', 'Cosmic Horror', 'Gothic', 'Supernatural']
  },
  {
    name: 'Other',
    options: ['Western', 'Historical', 'Action Adventure', 'Literary Fiction', 'Comedy', 'Satire']
  }
];

const StorySetup: React.FC<StorySetupProps> = ({ onInitialize, isLoading, onResume }) => {
  const [seed, setSeed] = useState<StorySeed>({
    title: '',
    genre: [{ name: 'Cyberpunk', weight: 'primary' }],
    premise: {
      surface: '',
      tension: '',
      change: ''
    },
    characters: '',
    worldLore: '',
    style: 'Gritty, emotional, cinematic, third-person limited. Strong dialogue, vivid settings, no modern slang.',
    mode: StoryMode.NOVEL,
    targetChapters: 12,
    chapterLength: ChapterLength.STANDARD,
    contentBoundaries: {
      audience: 'Adult',
      romanceHeat: 'None',
      violenceLevel: 'Low',
      language: 'Moderate',
      darkThemes: 'None'
    }
  });

  const [customGenre, setCustomGenre] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    const finalValue = name === 'targetChapters' ? parseInt(value) || 1 : value;
    setSeed(prev => ({ ...prev, [name]: finalValue }));
  };

  const handleBoundaryChange = (field: string, value: string) => {
    setSeed(prev => {
      const nextBoundaries = { ...(prev.contentBoundaries || {}), [field]: value };
      
      // Safety validation logic
      const isYouth = nextBoundaries.audience === 'Young Adult' || nextBoundaries.audience === 'Middle Grade';
      const isAdultHeat = nextBoundaries.romanceHeat === 'Spicy Romance' || nextBoundaries.romanceHeat === 'Open-Door Romance';
      
      if (isYouth && isAdultHeat) {
        nextBoundaries.romanceHeat = 'Closed-Door Romance';
      }
      
      return { ...prev, contentBoundaries: nextBoundaries as any };
    });
  };

  const handlePremiseChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setSeed(prev => ({
      ...prev,
      premise: { ...prev.premise, [name]: value }
    }));
  };

  const toggleGenre = (genreName: string) => {
    setSeed(prev => {
      const existingIndex = prev.genre.findIndex(g => g.name === genreName);
      let newGenres = [...prev.genre];

      if (existingIndex >= 0) {
        newGenres.splice(existingIndex, 1);
      } else {
        newGenres.push({ name: genreName, weight: 'primary' });
      }
      return { ...prev, genre: newGenres };
    });
  };

  const addCustomGenre = () => {
    if (customGenre && !seed.genre.find(g => g.name === customGenre)) {
      setSeed(prev => ({ ...prev, genre: [...prev.genre, { name: customGenre, weight: 'primary' }] }));
      setCustomGenre('');
    }
  };

  const getGenreStyle = (genreName: string) => {
    const genre = seed.genre.find(g => g.name === genreName);
    if (!genre) return 'bg-cv-panel border-cv-border text-cv-muted hover:border-cv-border-strong';
    return 'bg-cv-interactive border-cv-interactive text-cv-page shadow-lg shadow-black/40';
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (seed.title && seed.premise.surface && seed.genre.length > 0) {
      onInitialize(seed);
    }
  };

  const modes = [
    { id: StoryMode.NOVEL, label: 'Novel', desc: 'Deep prose narrative.' }
  ];

  const lengths = [
    { id: ChapterLength.STANDARD, label: 'Standard', desc: 'Traditional novel chapter (industry norm)n2,000–3,500 words' }
  ];

  return (
    <div className="min-h-screen bg-cv-page flex items-center justify-center p-6 text-cv-primary overflow-y-auto">
      <div className="max-w-6xl w-full bg-cv-panel border border-cv-border rounded-2xl p-8 my-8 shadow-2xl relative">
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-cv-gold via-cv-gold-soft to-cv-gold"></div>
        
        <header className="mb-6 flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-black text-cv-gold tracking-tighter uppercase flex items-center gap-3">
              Create a New Book
              <Tooltip text="The Story Blueprint stores your foundational story idea, genre, and world rules. It's the primary context for the AI." />
            </h1>
            <p className="text-cv-muted font-mono text-[10px] mt-1 uppercase tracking-widest">Set up your story blueprint, chapter plan, and writing style.</p>
            <p className="text-cv-gold text-sm mt-3 font-medium">You do not need every detail. A few strong ideas are enough to start.</p>
          </div>
          <button onClick={onResume} className="p-2 text-cv-muted hover:text-cv-primary transition-colors bg-cv-elevated rounded-lg border border-cv-border-strong">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </header>

        <div className="flex items-center gap-4 text-[10px] sm:text-xs font-bold uppercase tracking-wider text-cv-muted mb-10 overflow-x-auto pb-2 border-b border-cv-border/50">
          <span className="text-cv-gold flex py-2 items-center gap-2 border-b-2 border-cv-interactive whitespace-nowrap">1. Book Setup</span>
          <span className="flex py-2 items-center gap-2 border-b-2 border-transparent whitespace-nowrap">2. Content Boundaries</span>
          <span className="flex py-2 items-center gap-2 border-b-2 border-transparent whitespace-nowrap">3. Story Core</span>
          <span className="flex py-2 items-center gap-2 border-b-2 border-transparent whitespace-nowrap">4. Characters & World</span>
          <span className="flex py-2 items-center gap-2 border-b-2 border-transparent whitespace-nowrap">5. Style & Roadmap</span>
        </div>

        <form onSubmit={handleSubmit} className="space-y-12">
          {/* Section 1: Core Logistics */}
          <section className="space-y-6">
            <h2 className="text-[11px] font-black text-cv-gold uppercase tracking-[0.2em] border-b border-cv-border pb-2 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-cv-gold"></span> 1. Book Setup
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="md:col-span-2 space-y-2">
                <label className="text-[10px] font-bold text-cv-muted uppercase">Book Title</label>
                <input name="title" value={seed.title} onChange={handleChange} className="w-full bg-cv-page border border-cv-border rounded-lg p-3 focus:border-cv-interactive outline-none transition-all font-bold text-cv-primary placeholder:text-cv-muted" placeholder="Enter your book title" required />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-bold text-cv-muted uppercase">Number of Chapters</label>
                <input type="number" name="targetChapters" value={seed.targetChapters} onChange={handleChange} min="1" max="100" className="w-full bg-cv-page border border-cv-border rounded-lg p-3 focus:border-cv-interactive outline-none transition-all text-cv-primary font-mono" required />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-3">
                <label className="text-[10px] font-bold text-cv-muted uppercase">Format</label>
                <div className="grid grid-cols-1 gap-2 max-w-[240px]">
                  {modes.map(m => (
                    <button key={m.id} type="button" onClick={() => setSeed(prev => ({ ...prev, mode: m.id }))} className={`p-3 rounded-lg border text-center transition-all ${seed.mode === m.id ? 'bg-cv-success/20 border-cv-interactive text-cv-gold shadow-[0_0_15px_rgba(59,130,246,0.1)]' : 'bg-cv-page border-cv-border text-cv-disabled hover:border-cv-border-strong'}`}>
                      <div className="text-[10px] font-black uppercase tracking-tighter">{m.label}</div>
                    </button>
                  ))}
                </div>
              </div>
              <div className="space-y-3">
                <label className="text-[10px] font-bold text-cv-muted uppercase">Chapter Length</label>
                <div className="grid grid-cols-1 gap-2 max-w-[240px]">
                  {lengths.map(l => (
                    <button key={l.id} type="button" onClick={() => setSeed(prev => ({ ...prev, chapterLength: l.id }))} className={`p-3 rounded-lg border text-center transition-all ${seed.chapterLength === l.id ? 'bg-cv-interactive/15 border-cv-interactive text-cv-interactive' : 'bg-cv-page border-cv-border text-cv-disabled hover:border-cv-border-strong'}`}>
                      <div className="text-[10px] font-black uppercase tracking-tighter">{l.label}</div>
                      <div className="text-[9px] text-cv-muted mt-1 whitespace-pre-line leading-tight">{l.desc}</div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
            
            <div className="space-y-4">
              <label className="text-[10px] font-bold text-cv-muted uppercase flex justify-between">
                <span>Genre</span>
                <span className="text-cv-disabled">Choose one or more genres</span>
              </label>
              <div className="bg-cv-page border border-cv-border rounded-xl p-6 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {GENRE_CATEGORIES.map(cat => (
                    <details key={cat.name} className="group bg-cv-panel/50 border border-cv-border rounded-lg overflow-hidden [&_summary::-webkit-details-marker]:hidden" open={cat.name === 'Speculative' || cat.name === 'Fantasy'}>
                      <summary className="p-3 text-[10px] font-bold text-cv-muted uppercase tracking-widest cursor-pointer hover:bg-cv-elevated flex justify-between items-center list-none">
                        {cat.name}
                        <svg className="w-4 h-4 text-cv-muted group-open:rotate-180 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                      </summary>
                      <div className="p-3 flex flex-wrap gap-1.5 border-t border-cv-border/50 bg-cv-panel/20">
                        {cat.options.map(opt => {
                          return (
                            <button
                              key={opt}
                              type="button"
                              onClick={() => toggleGenre(opt)}
                              className={`px-3 py-1.5 text-[9px] font-bold rounded uppercase transition-all border flex items-center ${getGenreStyle(opt)}`}
                            >
                              {opt}
                            </button>
                          );
                        })}
                      </div>
                    </details>
                  ))}
                </div>
                <div className="pt-4 border-t border-cv-border/50 flex gap-2">
                   <input 
                    value={customGenre} 
                    onChange={(e) => setCustomGenre(e.target.value)} 
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addCustomGenre())} 
                    className="flex-1 bg-cv-panel border border-cv-border rounded-lg p-2 text-[10px] uppercase font-bold outline-none focus:border-cv-interactive text-cv-secondary placeholder:normal-case placeholder:font-normal placeholder:text-cv-muted"
                    placeholder="Add your own genre or blend, like cozy fantasy western" 
                  />
                  <button type="button" onClick={addCustomGenre} className="px-4 py-2 bg-cv-elevated hover:bg-cv-elevated border border-cv-border-strong rounded-lg text-[10px] font-black uppercase transition-colors text-cv-muted">Add Genre</button>
                </div>
              </div>
            </div>
          </section>

          {/* Section 2: Content Boundaries */}
          <section className="space-y-6">
            <h2 className="text-[11px] font-black text-cv-gold uppercase tracking-[0.2em] border-b border-cv-border pb-2 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-cv-gold"></span> 2. Content Boundaries
            </h2>
            <p className="text-xs font-medium text-cv-muted">Genre controls story market/category. Content Boundaries control heat level, violence, language, and dark themes.</p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="text-[10px] font-bold text-cv-muted uppercase">Target Audience</label>
                <select value={seed.contentBoundaries?.audience || 'Adult'} onChange={(e) => handleBoundaryChange('audience', e.target.value)} className="w-full bg-cv-page border border-cv-border rounded-lg p-3 text-sm text-cv-secondary outline-none focus:border-rose-500">
                  <option value="Adult">Adult</option>
                  <option value="New Adult">New Adult</option>
                  <option value="Young Adult">Young Adult</option>
                  <option value="Middle Grade">Middle Grade</option>
                </select>
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-bold text-cv-muted uppercase">Romance Heat Level</label>
                <select value={seed.contentBoundaries?.romanceHeat || 'None'} onChange={(e) => handleBoundaryChange('romanceHeat', e.target.value)} className="w-full bg-cv-page border border-cv-border rounded-lg p-3 text-sm text-cv-secondary outline-none focus:border-rose-500">
                  <option value="None">None</option>
                  <option value="Sweet Romance">Sweet Romance</option>
                  <option value="Closed-Door Romance">Closed-Door Romance</option>
                  {/* Validation check */}
                  {(seed.contentBoundaries?.audience === 'Young Adult' || seed.contentBoundaries?.audience === 'Middle Grade') ? (
                    <option disabled value="Open-Door Romance">Open-Door Romance (Adult Audience Only)</option>
                  ) : (
                    <option value="Open-Door Romance">Open-Door Romance</option>
                  )}
                  {(seed.contentBoundaries?.audience === 'Young Adult' || seed.contentBoundaries?.audience === 'Middle Grade') ? (
                    <option disabled value="Spicy Romance">Spicy Romance (Adult Audience Only)</option>
                  ) : (
                    <option value="Spicy Romance">Spicy Romance</option>
                  )}
                </select>
                {(seed.contentBoundaries?.audience === 'Young Adult' || seed.contentBoundaries?.audience === 'Middle Grade') && (
                  <p className="text-[10px] text-rose-400 font-medium">Spicy romance settings are only available for adult-audience projects with adult characters.</p>
                )}
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-bold text-cv-muted uppercase">Violence Level</label>
                <select value={seed.contentBoundaries?.violenceLevel || 'Low'} onChange={(e) => handleBoundaryChange('violenceLevel', e.target.value)} className="w-full bg-cv-page border border-cv-border rounded-lg p-3 text-sm text-cv-secondary outline-none focus:border-rose-500">
                  <option value="Low">Low</option>
                  <option value="Moderate">Moderate</option>
                  <option value="Graphic">Graphic</option>
                </select>
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-bold text-cv-muted uppercase">Language</label>
                <select value={seed.contentBoundaries?.language || 'Moderate'} onChange={(e) => handleBoundaryChange('language', e.target.value)} className="w-full bg-cv-page border border-cv-border rounded-lg p-3 text-sm text-cv-secondary outline-none focus:border-rose-500">
                  <option value="Clean">Clean</option>
                  <option value="Moderate">Moderate</option>
                  <option value="Strong">Strong</option>
                </select>
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-bold text-cv-muted uppercase">Dark Themes</label>
                <select value={seed.contentBoundaries?.darkThemes || 'None'} onChange={(e) => handleBoundaryChange('darkThemes', e.target.value)} className="w-full bg-cv-page border border-cv-border rounded-lg p-3 text-sm text-cv-secondary outline-none focus:border-rose-500">
                  <option value="None">None</option>
                  <option value="Moderate">Moderate</option>
                  <option value="Heavy">Heavy</option>
                </select>
              </div>
            </div>
          </section>

          {/* Section 3: Narrative DNA */}
          <section className="space-y-6">
            <h2 className="text-[11px] font-black text-cv-gold uppercase tracking-[0.2em] border-b border-cv-border pb-2 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-cv-gold"></span> 3. Story Core
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="space-y-2">
                <label className="text-[10px] font-bold text-cv-muted uppercase">Main Conflict</label>
                <div className="text-[9px] text-cv-disabled mb-1">What is happening on the surface?</div>
                <textarea name="surface" value={seed.premise.surface} onChange={handlePremiseChange} className="w-full bg-cv-page border border-cv-border rounded-lg p-4 h-32 focus:border-cv-interactive outline-none resize-none transition-all text-xs font-medium text-cv-primary placeholder:text-cv-muted" placeholder="What is happening explicitly?" required />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-bold text-cv-muted uppercase">Deeper Tension</label>
                <div className="text-[9px] text-cv-disabled mb-1">What emotional or moral pressure drives the story?</div>
                <textarea name="tension" value={seed.premise.tension} onChange={handlePremiseChange} className="w-full bg-cv-page border border-cv-border rounded-lg p-4 h-32 focus:border-cv-interactive outline-none resize-none transition-all text-xs font-medium text-cv-primary placeholder:text-cv-muted" placeholder="Why does it matter deeply?" required />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-bold text-cv-muted uppercase">Permanent Change</label>
                <div className="text-[9px] text-cv-disabled mb-1">What changes forever by the end?</div>
                <textarea name="change" value={seed.premise.change} onChange={handlePremiseChange} className="w-full bg-cv-page border border-cv-border rounded-lg p-4 h-32 focus:border-cv-interactive outline-none resize-none transition-all text-xs font-medium text-cv-primary placeholder:text-cv-muted" placeholder="What will be lost or gained forever?" required />
              </div>
            </div>
          </section>

          {/* Section 4: Characters and World */}
          <section className="space-y-6">
            <h2 className="text-[11px] font-black text-cv-gold uppercase tracking-[0.2em] border-b border-cv-border pb-2 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-cv-gold"></span> 4. Characters & World
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="text-[10px] font-bold text-cv-muted uppercase">Main Characters</label>
                <textarea name="characters" value={seed.characters} onChange={handleChange} className="w-full bg-cv-page border border-cv-border rounded-lg p-4 h-40 focus:border-cv-interactive outline-none resize-none transition-all text-sm text-cv-secondary placeholder:text-cv-muted" placeholder="List the important characters and what they want. Example: Mara Vale — stubborn scavenger, wants to save her brother." />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-bold text-cv-muted uppercase">World Rules & Lore</label>
                <textarea name="worldLore" value={seed.worldLore} onChange={handleChange} className="w-full bg-cv-page border border-cv-border rounded-lg p-4 h-40 focus:border-cv-interactive outline-none resize-none transition-all text-sm text-cv-secondary placeholder:text-cv-muted" placeholder="Describe the rules of the world, magic, technology, factions, locations, and limits." />
              </div>
            </div>
          </section>

          {/* Section 5: Style and Roadmap */}
          <section className="space-y-6">
            <h2 className="text-[11px] font-black text-cv-gold uppercase tracking-[0.2em] border-b border-cv-border pb-2 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-cv-gold"></span> 5. Style & Roadmap
            </h2>

            <div className="space-y-2">
              <label className="text-[10px] font-bold text-cv-muted uppercase">Writing Style</label>
              <textarea name="style" value={seed.style} onChange={handleChange} className="w-full bg-cv-page border border-cv-border rounded-lg p-4 h-20 focus:border-cv-interactive outline-none resize-none transition-all text-sm text-cv-secondary placeholder:text-cv-muted" placeholder="Gritty, emotional, cinematic, third-person limited. Strong dialogue, vivid settings, no modern slang." />
            </div>

            <div className="space-y-2">
              <label className="text-[10px] font-bold text-cv-muted uppercase">Custom Chapter Roadmap (Optional)</label>
              <div className="text-[9px] text-cv-disabled mb-1">Already know the chapter plan? Paste it here. If left blank, the Architect will create one for you.</div>
              <textarea name="premadeRoadmap" value={seed.premadeRoadmap || ''} onChange={handleChange} className="w-full bg-cv-page border border-cv-border rounded-lg p-4 h-40 focus:border-cv-success outline-none resize-none transition-all text-xs font-mono text-cv-success/80 placeholder:text-cv-muted" placeholder="Chapter 1: The incident... &#10;Chapter 2: The journey begins... &#10;Chapter 3: The climax..." />
            </div>
          </section>

          <button type="submit" disabled={isLoading || seed.genre.length === 0 || !seed.premise.surface} className="w-full py-5 bg-cv-interactive hover:bg-cv-interactive-hover text-cv-page font-black text-sm uppercase tracking-[0.3em] rounded-xl transition-all shadow-2xl shadow-black/40 transform active:scale-[0.98] disabled:opacity-30 flex items-center justify-center gap-3">
            {isLoading ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                Building Story Blueprint...
              </>
            ) : 'Build Story Blueprint'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default StorySetup;
